






























































































import Vue from "vue";

import IconLogout from "@joyz/app-core/assets/icons/default/LogOut.svg";
import IconBrowser from "@joyz/app-core/assets/icons/default/Browser.svg";

import {
  GET_ACCENT_OPTIONS,
  GET_SPEED_OPTIONS,
  GET_STT_CONNECTION_METHOD_OPTIONS,
  HELP_GUIDE_LINK,
  VUEX_USER_ACTION_NAMES,
  VUEX_USER_NAMESPACE,
  ROUTES,
  VUEX_ROOT_ACTION_NAMES
} from "@tt-app/constants";
import SettingsHeader from "@tt-app/components/user/settings/SettingsHeader.vue";
import SettingsFooter from "@tt-app/components/user/settings/SettingsFooter.vue";
import AccentCard from "@tt-app/components/user/settings/AccentCard.vue";
import SpeechSpeedCard from "@tt-app/components/user/settings/SpeechSpeedCard.vue";
import SttConnectionMethodCard from "@tt-app/components/user/settings/SttConnectionMethodCard.vue";
import {
  FormOptions,
  UserProfile,
  UserProfileAudioConfig,
  AudioAccent,
  AudioSpeed,
  UseOnlyHttp
} from "@tt-app/models";
import { getLabelForOptionValue } from "@tt-app/utils";

import PackageInfo from "../../../package.json";
import GitInfo from "../../../GitInfo.json";

const { ALIASES_REQUEST, LOAD_USER_PROFILE, UPDATE_USER_PROFILE } = VUEX_USER_ACTION_NAMES;

type SettingMenu =
  | "accent"
  | "speech-speed"
  | "terms-of-service"
  | "privacy-policy"
  | "help-guide"
  | "release-notes"
  | "stt-connection-method"
  | "lms-redirect";

interface State {
  iconLogout: string;
  iconBrowser: string;
  isLoading: boolean;
  modal: {
    show: boolean;
    component: "accent-card" | "speech-speed-card" | "stt-connection-method-card";
    audioConfig: UserProfileAudioConfig;
    useOnlyHttp: boolean;
  };
}

interface Method {
  onModalCancel: () => void;
  onModalOK: (newVal: number) => Promise<void>;
  onSettingMenuClick: (menu: SettingMenu) => void;
  logout: () => void;
}

interface Computed {
  commitHash: string;
  version: string;
  accentOptions: FormOptions<number>[];
  speedOptions: FormOptions<number>[];
  sttConnectionMethod?: string;
  sttConnectionMethodOptions: FormOptions<boolean>[];
  userProfile?: UserProfile;
  userAccent?: string;
  userSpeed?: string;
  userOrgHash?: string;
  isStaff?: boolean;
}

export default Vue.extend<State, Method, Computed, {}>({
  name: "UserSettings",
  components: {
    AccentCard,
    SpeechSpeedCard,
    SettingsHeader,
    SettingsFooter,
    SttConnectionMethodCard
  },

  created() {
    this.$store.dispatch(`${VUEX_USER_NAMESPACE}/${ALIASES_REQUEST}`);
    this.$store.dispatch(`${VUEX_USER_NAMESPACE}/${LOAD_USER_PROFILE}`);
  },

  data() {
    return {
      iconLogout: IconLogout,
      iconBrowser: IconBrowser,
      isLoading: false,
      modal: {
        show: false,
        component: "accent-card",
        audioConfig: { accent: 0, speed: 90 },
        useOnlyHttp: false
      }
    };
  },

  computed: {
    version() {
      return (PackageInfo as any)["version"];
    },
    accentOptions() {
      return GET_ACCENT_OPTIONS(this);
    },
    commitHash() {
      return (GitInfo as any)["commitHash"];
    },
    speedOptions() {
      return GET_SPEED_OPTIONS(this);
    },
    sttConnectionMethodOptions() {
      return GET_STT_CONNECTION_METHOD_OPTIONS(this);
    },
    sttConnectionMethod() {
      const sttConnectionMethod = this.userProfile ? this.userProfile.useOnlyHttp : -1;
      return getLabelForOptionValue(this.sttConnectionMethodOptions, sttConnectionMethod);
    },
    userProfile() {
      return this.$store.state.user.profile as UserProfile | undefined;
    },
    userAccent() {
      const accent = this.userProfile ? this.userProfile.audio.accent : -1;
      return getLabelForOptionValue(this.accentOptions, accent);
    },
    userSpeed() {
      const speed = this.userProfile ? this.userProfile.audio.speed : -1;
      return getLabelForOptionValue(this.speedOptions, speed);
    },
    userOrgHash() {
      return this.userProfile?.org?.hash;
    },
    isStaff() {
      if (!this.userProfile) return false;
      return this.userProfile.isStaff || false;
    }
  },

  methods: {
    onModalCancel() {
      this.modal.show = false;
    },
    async onModalOK(newValue: number | boolean) {
      this.modal.show = false;

      const settings: Pick<UserProfile, "audio" | "useOnlyHttp"> = {
        audio: {
          ...this.modal.audioConfig
        },
        useOnlyHttp: this.modal.useOnlyHttp
      };

      switch (this.modal.component) {
        case "accent-card":
          settings.audio.accent = newValue as AudioAccent;
          break;
        case "speech-speed-card":
          settings.audio.speed = newValue as AudioSpeed;
          break;
        case "stt-connection-method-card":
          settings.useOnlyHttp = newValue as UseOnlyHttp;
          break;
      }

      this.isLoading = true;
      await this.$store.dispatch(`${VUEX_USER_NAMESPACE}/${UPDATE_USER_PROFILE}`, settings);
      this.isLoading = false;
    },

    onSettingMenuClick(menu) {
      // Redirect to TOS page
      if (menu === "terms-of-service") {
        this.$router.push({ name: ROUTES.USER.TERMS_OF_SERVICES.NAME });
      }
      // Redirect to Privacy policy page
      else if (menu === "privacy-policy") {
        this.$router.push({ name: ROUTES.USER.PRIVACY_POLICY.NAME });
      }
      // Help guide
      else if (menu === "help-guide") {
        window.open(HELP_GUIDE_LINK, "_blank", "noopener noreferrer");
      }
      // Release Notes
      else if (menu === "release-notes") {
        this.$router.push({ name: ROUTES.RELEASE_NOTES.RELEASE_NOTES.NAME });
      }
      // Redirect to LMS
      else if (menu === "lms-redirect") {
        window.open(`${process.env.VUE_APP_LMS}${this.userOrgHash}`, "_blank");
      }
      // Show some modal
      else if (menu === "accent" || menu === "speech-speed" || menu === "stt-connection-method") {
        // load audio config
        if (this.userProfile) {
          this.modal.audioConfig = this.userProfile.audio;
          this.modal.useOnlyHttp = this.userProfile.useOnlyHttp;
        }

        // load component
        switch (menu) {
          case "accent":
            this.modal.component = "accent-card";
            break;
          case "speech-speed":
            this.modal.component = "speech-speed-card";
            break;
          case "stt-connection-method":
            this.modal.component = "stt-connection-method-card";
            break;
        }

        // show modal
        this.modal.show = true;
      }
    },

    logout() {
      this.$store.dispatch(`${VUEX_ROOT_ACTION_NAMES.LOGOUT_REQUEST}`);
    }
  }
});
